import * as React from 'react';
import Draggable from 'react-draggable';
import { AutoSizer, Grid, GridCellProps, ScrollbarPresenceParams, ScrollParams, Size } from 'react-virtualized';
import {
  IMediaItemDuration,
  IViewModeColumn,
  MediaItem,
  MediaType,
  TimelineDataItem,
  TreeItem,
  ViewModeType
} from '../../store/types';
import { LoadingSpinner } from '../common/loadingSpinner';
import { TreeNode } from './TreeNode';

const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 52;

export interface TimelineProps {
  isLoading: boolean;
  tree: TreeItem[];
  medias: { [id: number]: MediaItem };
  columns: IViewModeColumn[];
  itemsSource: TimelineDataItem[][];
  parentMediaId: number;
  startDate: Date;
  endDate: Date;
  viewModeType: ViewModeType;
}

export interface TimelineActions {
  getData: (parentMediaId: number) => void;
  collapse: (treeId: string) => void;
  expand: (treeId: string) => void;
  mediaSelected: (mediaId: number) => void;
}

export interface TimelineState {
  horizontalScrollPresent: boolean;
  verticalScrollPresent: boolean;
  scrollbarSize: number;

  treeColumnWidth: number;
  selectedTreeItem: string;
}

type AllProps = TimelineProps & TimelineActions;

export class Timeline extends React.PureComponent<AllProps, TimelineState> {
  private mediaListGrid: React.RefObject<Grid>;
  private headerGrid: React.RefObject<Grid>;
  private contentGrid: React.RefObject<Grid>;
  private onScrollbarChangeTimer: number;


  constructor(props: AllProps) {
    super(props);
    const { parentMediaId, getData } = this.props;
    getData(parentMediaId);
    this.mediaListGrid = React.createRef<Grid>();
    this.headerGrid = React.createRef<Grid>();
    this.contentGrid = React.createRef<Grid>();
    this.state = {
      horizontalScrollPresent: false,
      verticalScrollPresent: false,
      scrollbarSize: 0,
      selectedTreeItem: '',
      treeColumnWidth: 400
    };
    this.rowClick = this.rowClick.bind(this);
    this.onScrollbarChangeTimer = 0;
  }

  public toggleNode = (e: React.MouseEvent<HTMLElement>, rowIndex: number, isExpanded: boolean) => {
    e.stopPropagation();

    const item = this.props.tree[rowIndex];
    if (!item.hasChildren) {
      return;
    }

    isExpanded ? this.props.collapse(item.treeId) : this.props.expand(item.treeId);
  };

  private rowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rowIndex = e.currentTarget.getAttribute('data-key');
    if (!rowIndex) {
      return;
    }
    const treeItem = this.props.tree[rowIndex];
    if (treeItem.level === 1) {
      return;
    }
    this.setState({ ...this.state, selectedTreeItem: treeItem.treeId });
    this.props.mediaSelected(treeItem.id);
  };

  public headerCellRenderer = (params: GridCellProps) => (
    <div
      key={params.key}
      style={params.style}
      className={`cell header ${this.props.columns[params.columnIndex].headerCellClassModifier}`}
    >
      <p className={`header-subtitle--${this.props.columns[params.columnIndex].headerSubtitleClassModifier}`}>
        {this.props.columns[params.columnIndex].subtitle}
      </p>
      <p className={`header-title--${this.props.columns[params.columnIndex].headerTitleClassModifier}`}>
        {this.props.columns[params.columnIndex].title}
      </p>
    </div>
  );

  public mediaCellRenderer = (params: GridCellProps) => (
    <div
      key={params.key}
      style={params.style}
      className={`cell mediaCell${
        this.state.selectedTreeItem === this.props.tree[params.rowIndex].treeId ? ' selected' : ''
      }`}
      data-key={params.rowIndex}
      onClick={this.rowClick}
    >
      <TreeNode
        item={this.props.tree[params.rowIndex]}
        isHighlighted={false}
        media={this.props.medias[this.props.tree[params.rowIndex].id]}
        onClick={e => this.toggleNode(e, params.rowIndex, this.props.tree[params.rowIndex].isExpanded || false)}
      />
    </div>
  );

  public contentCellRenderer = (params: GridCellProps) => (
    <div
      key={params.key}
      style={params.style}
      className={`cell ${this.props.columns[params.columnIndex].contentCellClassModifier}${
        this.state.selectedTreeItem === this.props.tree[params.rowIndex].treeId ? ' selected' : ''
      }`}
      data-key={params.rowIndex}
      onClick={this.rowClick}
    >
      {this.props.itemsSource[params.rowIndex][params.columnIndex] &&
        this.props.itemsSource[params.rowIndex][params.columnIndex].durations.map(
          (dataItem: IMediaItemDuration, i: number) => {
            let barTypeClass = 'durationBar--media';
            switch (this.props.itemsSource[params.rowIndex][params.columnIndex].mediaType) {
              case MediaType.AdvancedLocalInputPlaylistChild:
              case MediaType.AdvancedLocalInputPlaylistParent:
              case MediaType.Playlist:
              case MediaType.EventPlaylist:
                barTypeClass = 'durationBar--playlist';
                break;
              case MediaType.AdvancedLocalInputTagChild:
              case MediaType.AdvancedLocalInputTagParent:
              case MediaType.Tag:
                barTypeClass = 'durationBar--tag';
                break;
            }
            return (
              <div
                key={params.key + i.toString()}
                className={`durationBar ${barTypeClass}${
                  dataItem.insideValidRange ? '' : ' durationBar--outsideRange'
                }`}
                style={{
                  left: dataItem.start * this.props.columns[params.columnIndex].width,
                  width:
                    dataItem.end * this.props.columns[params.columnIndex].width -
                    dataItem.start * this.props.columns[params.columnIndex].width
                }}
              />
            );
          }
        )}
      {this.props.columns[params.columnIndex].includesNow && (
        <div
          className="nowBar"
          style={{
            height: ROW_HEIGHT,
            left: this.props.columns[params.columnIndex].nowPosition
          }}
        />
      )}
    </div>
  );

  private mediaColumnWidthChanged = (deltaX: number) => {
    const treeColumnWidth = Math.max(40, Math.min(700, this.state.treeColumnWidth + deltaX));
    this.setState({ ...this.state, treeColumnWidth });
  };

  public customScrollSyncMediaGrid = (params: ScrollParams) => {
    const grid = this.contentGrid.current;
    if (grid) {
      grid.scrollToPosition({ scrollTop: params.scrollTop, scrollLeft: grid.state.scrollLeft });
    }
  };
  public customScrollSyncHeaderGrid = (params: ScrollParams) => {
    const grid = this.contentGrid.current;
    if (grid) {
      grid.scrollToPosition({ scrollTop: grid.state.scrollTop, scrollLeft: params.scrollLeft });
    }
  };
  public customScrollSyncContentGrid = (params: ScrollParams) => {
    const mediaGrid = this.mediaListGrid.current;
    const headerGrid = this.headerGrid.current;
    if (mediaGrid) {
      mediaGrid.scrollToPosition({ scrollTop: params.scrollTop, scrollLeft: mediaGrid.state.scrollLeft });
    }
    if (headerGrid) {
      headerGrid.scrollToPosition({ scrollTop: headerGrid.state.scrollTop, scrollLeft: params.scrollLeft });
    }
  };
  public onScrollbarPresenceChange = (params: ScrollbarPresenceParams) => {

    if (this.onScrollbarChangeTimer) {
      window.clearTimeout(this.onScrollbarChangeTimer);
    }

     /**
      * We use a timeout because in case of a window resize
      * there's a certain scenario in which onScrollbarPresenceChange
      * is called repeatedly and that causes this.setState() to be
      * called until React crashes.
      * 
      * This timeout seems to fix the issue.
      */ 
    this.onScrollbarChangeTimer = window.setTimeout(() => {
      const grid = this.contentGrid.current;
      const needsNewState =
      params.horizontal !== this.state.horizontalScrollPresent ||
      params.vertical !== this.state.verticalScrollPresent ||
      params.size !== this.state.scrollbarSize;
      
      if (grid && needsNewState) {
        this.setState({
          ...this.state,
          horizontalScrollPresent: params.horizontal,
          verticalScrollPresent: params.vertical,
          scrollbarSize: params.size
        });
      }

      // not reaquired, but whatever...
      window.clearTimeout(this.onScrollbarChangeTimer);
      this.onScrollbarChangeTimer = 0;
    }, 200);
  };

  private draggStop = (_: any, data: any) => this.mediaColumnWidthChanged(data.x);

  public render() {
    const noMedias = Object.keys(this.props.medias).length === 0;
    const colWidth = this.props.columns.length > 0 ? this.props.columns[0].width : 0;
    // const totalColWidth = this.props.columns.length * colWidth;

    const { scrollbarSize, horizontalScrollPresent, verticalScrollPresent } = this.state;
    return this.props.isLoading || noMedias ? (
      <LoadingSpinner />
    ) : (
      <div className="ap-timeline">
        <div
          className="ap-timeline-mediaColumn"
          style={{ marginTop: HEADER_HEIGHT, flexBasis: this.state.treeColumnWidth }}
        >
          <AutoSizer disableWidth>
            {(size: Size) => {
              const gridHeight = HEADER_HEIGHT + (horizontalScrollPresent ? size.height - scrollbarSize : size.height);
              const gridWidth = this.state.treeColumnWidth + (verticalScrollPresent ? scrollbarSize : 0);
              const wrapperHeight = gridHeight;
              const wrapperWidth = this.state.treeColumnWidth;
              return (
                <div
                  className="mediaGridScrollWrapper"
                  style={{
                    height: wrapperHeight,
                    width: wrapperWidth,
                    borderBottom: verticalScrollPresent ? '1px solid #e0e0e0' : ''
                  }}
                >
                  <Grid
                    ref={this.mediaListGrid}
                    containerStyle={{ width: wrapperWidth - 1 }}
                    className="ap-mediaGrid"
                    cellRenderer={this.mediaCellRenderer}
                    columnCount={1}
                    rowCount={this.props.tree.length}
                    columnWidth={this.state.treeColumnWidth}
                    width={gridWidth}
                    height={gridHeight}
                    rowHeight={ROW_HEIGHT}
                    onScroll={this.customScrollSyncMediaGrid}
                    // these don't exist on the Grid
                    selectedRowIndex={this.state.selectedTreeItem}
                  />
                </div>
              );
            }}
          </AutoSizer>
        </div>
        <Draggable
          axis="x"
          defaultClassName="DragHandle"
          defaultClassNameDragging="DragHandleActive"
          position={{ x: 0, y: 0 }}
          onStop={this.draggStop}
        >
          <div className="DragHandleIcon" />
        </Draggable>
        <div className="ap-timeline-contentColumn">
          <AutoSizer>
            {(size: Size) => {
              return (
                <div
                  style={{
                    // width: size.width - this.state.treeColumnWidth,
                    borderBottom: !horizontalScrollPresent && verticalScrollPresent ? '1px solid #e0e0e0' : ''
                  }}
                >
                  <Grid
                    ref={this.headerGrid}
                    className="ap-headerGrid"
                    containerStyle={{
                      borderRight: horizontalScrollPresent && !verticalScrollPresent ? '1px solid #e0e0e0' : ''
                    }}
                    startDate={this.props.startDate}
                    endDate={this.props.endDate}
                    cellRenderer={this.headerCellRenderer}
                    columnCount={this.props.columns.length}
                    columnWidth={colWidth}
                    height={HEADER_HEIGHT}
                    rowCount={1}
                    rowHeight={HEADER_HEIGHT}
                    width={size.width - (!horizontalScrollPresent ? scrollbarSize : 0)}
                    onScroll={this.customScrollSyncHeaderGrid}
                  />
                  <Grid
                    ref={this.contentGrid}
                    className="ap-contentGrid"
                    startDate={this.props.startDate}
                    endDate={this.props.endDate}
                    cellRenderer={this.contentCellRenderer}
                    columnCount={this.props.columns.length}
                    overscanColumnCount={3}
                    columnWidth={colWidth}
                    onScrollbarPresenceChange={this.onScrollbarPresenceChange}
                    height={size.height}
                    rowCount={this.props.tree.length}
                    rowHeight={ROW_HEIGHT}
                    width={size.width + (horizontalScrollPresent && verticalScrollPresent ? scrollbarSize : 0)}
                    onScroll={this.customScrollSyncContentGrid}
                    // these don't exist on the Grid
                    selectedRowIndex={this.state.selectedTreeItem}
                  />
                </div>
              );
            }}
          </AutoSizer>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/timeline/Timeline.tsx